// Smart Device Files Storage
// Temporary in-memory storage for document files during the policy flow
// Files are stored here because Redux cannot serialize File/Blob objects

// Storage map for smart device documents
const smartDeviceFiles = new Map();

// File keys for smart device documents
export const SMART_DEVICE_FILE_KEYS = {
  IMEI: 'smartDevice_imei',
  CPR_FRONT: 'smartDevice_cpr_front',
  CPR_BACK: 'smartDevice_cpr_back',
};

/**
 * Store a file for smart device policy
 * @param {string} key - File key (use SMART_DEVICE_FILE_KEYS)
 * @param {File} file - The file to store
 */
export const storeSmartDeviceFile = (key, file) => {
  if (file instanceof File || file instanceof Blob) {
    smartDeviceFiles.set(key, file);
    console.log('[SmartDevice Storage] Stored file:', key, file.name || 'blob');
  }
};

/**
 * Get a stored file
 * @param {string} key - File key
 * @returns {File|null} The stored file or null
 */
export const getSmartDeviceFile = (key) => {
  return smartDeviceFiles.get(key) || null;
};

/**
 * Get all stored smart device files
 * @returns {Object} Object with all stored files { imei, cprFront, cprBack }
 */
export const getAllSmartDeviceFiles = () => {
  return {
    imei: smartDeviceFiles.get(SMART_DEVICE_FILE_KEYS.IMEI) || null,
    cprFront: smartDeviceFiles.get(SMART_DEVICE_FILE_KEYS.CPR_FRONT) || null,
    cprBack: smartDeviceFiles.get(SMART_DEVICE_FILE_KEYS.CPR_BACK) || null,
  };
};

/**
 * Get all files as an array for Vista submission
 * @returns {File[]} Array of files in order [imei, cprFront, cprBack]
 */
export const getSmartDeviceFilesArray = () => {
  const files = [];
  const imei = smartDeviceFiles.get(SMART_DEVICE_FILE_KEYS.IMEI);
  const cprFront = smartDeviceFiles.get(SMART_DEVICE_FILE_KEYS.CPR_FRONT);
  const cprBack = smartDeviceFiles.get(SMART_DEVICE_FILE_KEYS.CPR_BACK);

  if (imei) files.push(imei);
  if (cprFront) files.push(cprFront);
  if (cprBack) files.push(cprBack);

  return files;
};

/**
 * Get document types array matching the files order
 * @returns {string[]} Array of document type labels
 */
export const getSmartDeviceDocumentTypes = () => {
  const types = [];
  if (smartDeviceFiles.has(SMART_DEVICE_FILE_KEYS.IMEI)) types.push('Device Info');
  if (smartDeviceFiles.has(SMART_DEVICE_FILE_KEYS.CPR_FRONT)) types.push('CPR (Front)');
  if (smartDeviceFiles.has(SMART_DEVICE_FILE_KEYS.CPR_BACK)) types.push('CPR (Back)');
  return types;
};

/**
 * Remove a specific file
 * @param {string} key - File key to remove
 */
export const removeSmartDeviceFile = (key) => {
  smartDeviceFiles.delete(key);
  console.log('[SmartDevice Storage] Removed file:', key);
};

/**
 * Clear all stored smart device files
 * Call this after successful policy creation or when user exits flow
 */
export const clearSmartDeviceFiles = () => {
  smartDeviceFiles.clear();
  console.log('[SmartDevice Storage] Cleared all files');
};

/**
 * Get count of stored files
 * @returns {number} Number of stored files
 */
export const getSmartDeviceFilesCount = () => {
  return smartDeviceFiles.size;
};

/**
 * Check if all required files are stored
 * @returns {boolean} True if all 3 files are present
 */
export const hasAllSmartDeviceFiles = () => {
  return (
    smartDeviceFiles.has(SMART_DEVICE_FILE_KEYS.IMEI) &&
    smartDeviceFiles.has(SMART_DEVICE_FILE_KEYS.CPR_FRONT) &&
    smartDeviceFiles.has(SMART_DEVICE_FILE_KEYS.CPR_BACK)
  );
};

export default {
  storeSmartDeviceFile,
  getSmartDeviceFile,
  getAllSmartDeviceFiles,
  getSmartDeviceFilesArray,
  getSmartDeviceDocumentTypes,
  removeSmartDeviceFile,
  clearSmartDeviceFiles,
  getSmartDeviceFilesCount,
  hasAllSmartDeviceFiles,
  SMART_DEVICE_FILE_KEYS,
};
